/**
 * Created by Yoyo Fang on 08/08/2019.
 * Description:
 * container of different types of tiles, making use of component factory to generate specific type of component on runtime
 * ------ maintenance history ------
 * 08/13/2019 Yoyo     - as column chart is refactored as plugined service, add initialization logic for config of tile instance
 * 09/05/2019 Yoyo     - loading spinner for value tile needs subscription on actionSubject to get closed
 * 09/05/2019 Yoyo     - component config init logic move from parent to here
 */
import { Component, OnInit, Input, OnDestroy, ChangeDetectorRef, ViewChild, ElementRef, AfterViewChecked, ComponentRef } from '@angular/core';
import { TamDashboardTileType, TamDashboardActions, TamDashboardEvents, TileIcons } from '../tam-dashboard.model';
import { Subject, Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { DynamicComponentService } from '../../../services/dynamic-component.service';
import { TamDashboardPieChartTileComponent } from '../tam-dashboard-pie-chart-tile/tam-dashboard-pie-chart-tile.component';
import { TamDashboardTileBaseComponent } from '../tam-dashboard-tile.base.component';
import { TamDashboardColumnChartTileComponent } from '../tam-dashboard-column-chart-tile/tam-dashboard-column-chart-tile.component';
import { TamDashboardLineChartTileComponent } from '../tam-dashboard-line-chart-tile/tam-dashboard-line-chart-tile.component';
import { TamDashboardValueTileComponent } from '../tam-dashboard-value-tile/tam-dashboard-value-tile.component';
import { TamDashboardMetadataTileComponent } from '../tam-dashboard-metadata-tile/tam-dashboard-metadata-tile.component';
import { TamDashboardGridTileComponent } from '../tam-dashboard-grid-tile/tam-dashboard-grid-tile.component';
import { TamDashboardTileContainerDirective } from './tam-dashboard-tile-container.directive';
import { TamDashboardThreadTileComponent } from '../tam-dashboard-thread-tile/tam-dashboard-thread-tile.component';
import { TamDashboardRichTextTileComponent } from '../tam-dashboard-rich-text-tile/tam-dashboard-rich-text-tile.component';
import { ContextMenuConfig, ContextMenuShowAction } from '../../../widgets/tam-context-menu/tam-context-menu.model';
import * as _ from 'lodash';
import { TamDashboardMapTileComponent } from '../tam-dashboard-map-tile/tam-dashboard-map-tile.component';
import { StoreQuerierService } from '../../../services/store-querier.service';
import { getSystemConfigState } from '../../../redux/reducers/system-config-view.reducer';
import { CREATE_NOTE_SUCCESS, UPDATE_NOTE_SUCCESS } from '../../../tamalelibs/redux/note.actions';
import { lastCustomActionSelector } from '../../../redux/reducers/action-history.reducer';
import { Store, select } from '@ngrx/store';
import { AppState } from '../../../redux';

@Component({
    selector: 'tam-dashboard-tile-container',
    templateUrl: './tam-dashboard-tile-container.component.html',
    styleUrls: ['./tam-dashboard-tile-container.component.scss']
})
export class TamDashboardTileContainerComponent implements OnInit, OnDestroy, AfterViewChecked {

    @Input() actionSubject$: Subject<any>;
    @Input() feedbackSubject$: Subject<any>;
    @Input() id: string;
    @Input() name: string;
    @Input() type: TamDashboardTileType;
    @Input() icons: Array<object>;
    @Input() readonly: boolean;
    @ViewChild(TamDashboardTileContainerDirective, { static: true }) tileContainer: TamDashboardTileContainerDirective;
    @ViewChild('content', { static: false }) content: ElementRef;

    expandable = false;
    isActive = false;
    isLoading = false;
    isMapTileEnabled = false;
    noData = false;
    otherStatus = 'Expand';
    tileSettings: any = {};
    showIcons = false;
    banIcon = { 'Refresh': false };
    // noHeader = false;
    contextMenuConfig: ContextMenuConfig = null;

    private _component: ComponentRef<TamDashboardTileBaseComponent>;
    private _contentHeight = 0;
    private _destroySubscriptions: Array<Subscription> = [];
    private _initHeight = 0;
    private _contextMenuSubscription: Subscription = null;
    private _widget: any = null;
    private _tileSettingSubject = new Subject();

    constructor(
        private _changeDetectorRef: ChangeDetectorRef,
        private _storeQuerier: StoreQuerierService,
        private _store: Store<AppState>,
        private _dynamicComponentService: DynamicComponentService
    ) { }

    ngOnInit() {
        this._destroySubscriptions.push(
            this._store.pipe(
                select(lastCustomActionSelector)
            ).subscribe(action => this._actionMessageHandler(action)),

            this.actionSubject$.pipe(
                filter((action: any) => action && (action.id === this.id || action.type === TamDashboardActions.SET_TILE_ACTIVE)),
            ).subscribe(action => this._onSubscribeActions(action)),

            this._tileSettingSubject.pipe(
                filter((action: any) => action.type === 'CHANGE_TILE_SETTINGS'))
                .subscribe(() => {
                    this._changeTileSetting();
                })
        );
        this._initTileComponent();
    }

    ngOnDestroy() {
        this._destroySubscriptions.forEach(element => element.unsubscribe());
        if (this._contextMenuSubscription && !this._contextMenuSubscription.closed) {
            this._contextMenuSubscription.unsubscribe();
        }
    }

    ngAfterViewChecked() {
        if (!this.content || !this.content.nativeElement || !this.content.nativeElement.children ||
            !this.content.nativeElement.children[0] ||
            !this.content.nativeElement.children[0].children) {
            return;
        }
        const contentDom = this.content.nativeElement.children[0].children[0];
        if (contentDom && this._contentHeight !== contentDom.offsetHeight) {
            this._contentHeight = contentDom.offsetHeight;
        }
        if (this._initHeight === 0) {
            this._initHeight = this.content.nativeElement.offsetHeight;
        }
    }

    expand() {
        if (this.otherStatus === 'Expand') {
            this.otherStatus = 'Collapse';
            this.feedbackSubject$.next({
                id: this.id,
                type: TamDashboardEvents.EXPAND_COLLAPSE,
                payload: this._contentHeight + 61 // 37 -> height of title; 12*2-> paddings of body
            });
        } else {
            this.otherStatus = 'Expand';
            this.feedbackSubject$.next({
                id: this.id,
                type: TamDashboardEvents.EXPAND_COLLAPSE,
                payload: this._initHeight + 61
            });
        }
        this._changeDetectorRef.detectChanges();
    }

    iconClick(event: Event, icon) {
        event.stopPropagation();
        if (icon === TileIcons.HIDE_BORDER.icon) {
            this.tileSettings.hideBorder = !this.tileSettings.hideBorder;
            // tslint:disable-next-line: no-shadowed-variable
            const iconSettings = this.icons.filter(icon => icon['icon'] === TileIcons.HIDE_BORDER.icon);
            if (iconSettings && iconSettings.length) {
                iconSettings[0]['title'] = this.tileSettings.hideBorder ? 'Show Border' : 'Hide Border';
            }
            this._tileSettingSubject.next({ type: 'CHANGE_TILE_SETTINGS' });
        } else if (icon === TileIcons.HIDE_TITLE.icon) {
            this.tileSettings.hideHeader = !this.tileSettings.hideHeader;
            // tslint:disable-next-line: no-shadowed-variable
            const iconSettings = this.icons.filter(icon => icon['icon'] === TileIcons.HIDE_TITLE.icon);
            if (iconSettings && iconSettings.length) {
                iconSettings[0]['title'] = this.tileSettings.hideHeader ? 'Show Title' : 'Hide Title';
            }
            this._initIconMenuStatus();
            this._tileSettingSubject.next({ type: 'CHANGE_TILE_SETTINGS' });
        } else {
            this.feedbackSubject$.next({
                type: TamDashboardEvents.ITEM_ICON_CLICK,
                payload: {
                    id: this.id,
                    icon: icon
                }
            });
        }
    }

    onContainerClick(event) {
        this.feedbackSubject$.next({
            type: TamDashboardEvents.ITEM_CLICKED,
            id: this.id,
        });
    }

    onMouseenter() {
        this.showIcons = true;
    }

    onMouseleave() {
        this.showIcons = false;
    }

    showContextMenu(e) {
        this._changeDetectorRef.detectChanges();
        this.contextMenuConfig.show$.next(new ContextMenuShowAction(e, ''));
    }

    /**
     * refresh dashboard after adding notes
     * @param action
     */
    private _actionMessageHandler(action) {
        if (action.type === UPDATE_NOTE_SUCCESS || action.type === CREATE_NOTE_SUCCESS) {
            // add setTimeOut to make it run after creating notes for this corner case.
            setTimeout(() => {
                this.feedbackSubject$.next({
                    type: TamDashboardEvents.ITEM_ICON_CLICK,
                    payload: {
                        id: this.id,
                        icon: 'refresh'
                    }
                });
            }, 1000);
        }
    }

    private _onSubscribeActions(action) {
        switch (action.type) {
            case TamDashboardActions.SET_TILE_DATA: {
                this.noData = (!action.payload || action.payload.length === 0 ||
                    (action.payload.data && action.payload.data.length === 0))
                    && this.type !== TamDashboardTileType.VALUE && this.type !== TamDashboardTileType.EDITOR;

                // if the map tile was turned off, should not show the no data message.
                const systemConfig = this._storeQuerier.queryBySelector(getSystemConfigState);
                if (systemConfig && systemConfig.maptile) {
                    this.isMapTileEnabled = systemConfig.maptile['enable'];
                }

                if (this.type === TamDashboardTileType.MAP && !this.isMapTileEnabled) {
                    this.noData = false;
                }

                this.isLoading = false;
                this._changeDetectorRef.detectChanges();
                break;
            }
            case TamDashboardActions.SET_LOADING_STATUS: {
                this.isLoading = action.payload;
                this._changeDetectorRef.detectChanges();
                break;
            }
            case TamDashboardActions.LOAD_DATA: {
                this.isLoading = true;
                this._changeDetectorRef.detectChanges();
                break;
            }
            case TamDashboardActions.INIT_TILE: {
                if (action.payload.config.setWidget) {
                    action.payload.config.setWidget(action.payload.widget);
                }
                this._widget = action.payload.widget;
                this.tileSettings = this._prepareTileSettings();
                this.banIcon['Refresh'] = this.type === TamDashboardTileType.EDITOR;
                this._initIconMenuStatus();
                action.payload.config.init(this.id, this.readonly, this.name, this.actionSubject$, this.feedbackSubject$);
                if (this._component) {
                    this._component.instance.setConfig(action.payload.config);
                } else {
                    console.error('dynamic component not initialized');
                }
                if (this.isActive) {
                    // workaround for dashboard stream has to setTileActive before tile inited
                    this.actionSubject$.next({
                        id: this.id,
                        type: TamDashboardActions.SET_TILE_ACTIVE
                    });
                }
                break;
            }
            case TamDashboardActions.SET_TILE_TYPE: {
                const newType = parseInt(action.payload, 10);
                const typeChanged = (newType !== this.type);
                if (typeChanged) {
                    const prevLoading = this.isLoading;
                    this.isLoading = true;
                    if (this._component) {
                        this._component.destroy();
                    }
                    this.banIcon['Refresh'] = newType === TamDashboardTileType.EDITOR;
                    this._initTileComponent(newType);
                    this.isLoading = prevLoading;
                }
                this.feedbackSubject$.next({
                    id: this.id,
                    type: TamDashboardEvents.TYPE_CHANGED,
                    payload: {
                        changed: typeChanged,
                        type: newType
                    }
                });
                break;
            }
            case TamDashboardActions.SET_TILE_ACTIVE: {
                this.isActive = this.id === action.id;
                break;
            }
            case TamDashboardActions.PULL_TILE_SETTINGS: {
                // textarea tile handled this in tile.config
                const settings = _.cloneDeep(action.payload.visualization.settings);
                delete settings.hideHeader;
                delete settings.hideBorder;
                this.tileSettings = Object.assign({}, this.tileSettings, settings);
                this._changeTileSetting(true);
                break;
            }
        }
    }

    private _changeTileSetting(refreshTile = false) {
        if (!this._widget) {
            return;
        }
        this._widget.tile.visualization.settings = this.tileSettings;
        if (this.type !== TamDashboardTileType.EDITOR) {
            this.feedbackSubject$.next({
                type: TamDashboardEvents.TILE_SETTINGS_LOADED,
                id: this.id,
                payload: this._widget.tile,
                refreshTile: refreshTile
            });
        }
    }

    private _initTileComponent(type?) {
        this.expandable = false;
        switch (type !== undefined ? type : this.type) {
            case TamDashboardTileType.PIE_CHART:
                this._component = this._dynamicComponentService.loadComponent<TamDashboardPieChartTileComponent>
                    (this.tileContainer.viewContainerRef, TamDashboardPieChartTileComponent);
                break;
            case TamDashboardTileType.MAP:
                this._component = this._dynamicComponentService.loadComponent<TamDashboardMapTileComponent>
                    (this.tileContainer.viewContainerRef, TamDashboardMapTileComponent);
                break;
            case TamDashboardTileType.COLUMN_CHART:
            case TamDashboardTileType.BAR_CHART:
                this._component = this._dynamicComponentService.loadComponent<TamDashboardColumnChartTileComponent>
                    (this.tileContainer.viewContainerRef, TamDashboardColumnChartTileComponent);
                break;
            case TamDashboardTileType.LINE_CHART:
                this._component = this._dynamicComponentService.loadComponent<TamDashboardLineChartTileComponent>
                    (this.tileContainer.viewContainerRef, TamDashboardLineChartTileComponent);
                break;
            case TamDashboardTileType.VALUE:
                this._component = this._dynamicComponentService.loadComponent<TamDashboardValueTileComponent>
                    (this.tileContainer.viewContainerRef, TamDashboardValueTileComponent);
                break;
            case TamDashboardTileType.METADATA:
                this._component = this._dynamicComponentService.loadComponent<TamDashboardMetadataTileComponent>
                    (this.tileContainer.viewContainerRef, TamDashboardMetadataTileComponent);
                break;
            case TamDashboardTileType.GRID:
                this._component = this._dynamicComponentService.loadComponent<TamDashboardGridTileComponent>
                    (this.tileContainer.viewContainerRef, TamDashboardGridTileComponent);
                break;
            case TamDashboardTileType.NOTES:
                this._component = this._dynamicComponentService.loadComponent<TamDashboardThreadTileComponent>
                    (this.tileContainer.viewContainerRef, TamDashboardThreadTileComponent);
                this.expandable = true;
                break;
            case TamDashboardTileType.EDITOR:
                this._component = this._dynamicComponentService.loadComponent<TamDashboardRichTextTileComponent>
                    (this.tileContainer.viewContainerRef, TamDashboardRichTextTileComponent);
                // this.tileSettings.hideHeader = true;
                break;
        }
    }

    private _initContextMenu() {
        if (!this.contextMenuConfig) {
            this.contextMenuConfig = new ContextMenuConfig();
            this.contextMenuConfig.stateful = false;
            this.contextMenuConfig.showItemIcon = true;
            // should only subscribe once
            this._contextMenuSubscription = this.contextMenuConfig.events$.subscribe(this._contextMenuHandler.bind(this));
        }
        this.contextMenuConfig.items = [];
        if (this.expandable) {
            this.contextMenuConfig.items.push({ id: 'tile-expandable', icon: 'tile-' + this.otherStatus, text: this.otherStatus });
        }
        for (let i = 0; this.icons && i < this.icons.length; i++) {
            if (!this.banIcon[this.icons[i]['title']]) {
                this.contextMenuConfig.items.push({
                    id: this.icons[i]['title'],
                    icon: this.icons[i]['icon'],
                    text: this.icons[i]['title']
                });
            }
        }
    }

    private _contextMenuHandler(event) {
        // if not preventDefault, download file will open a new window
        event.payload.event.preventDefault();
        const icons = this.contextMenuConfig.items.filter(item => item.id === event.payload.id);
        if (icons && icons.length) {
            if (icons[0].id === 'tile-expandable') {
                this.expand();
                const menuItems = this.contextMenuConfig.items.filter(item => item.id === 'tile-expandable');
                if (menuItems.length > 0) {
                    menuItems[0].icon = 'tile-' + this.otherStatus;
                    menuItems[0].text = this.otherStatus;
                }
            } else if (icons[0].id === 'Hide Border' || icons[0].id === 'Show Border') {
                this.tileSettings.hideBorder = !this.tileSettings.hideBorder;
                const menuItems = this.contextMenuConfig.items.filter(item => item.id === 'Hide Border' || item.id === 'Show Border');
                if (menuItems.length > 0) {
                    menuItems[0].text = this.tileSettings.hideBorder ? 'Show Border' : 'Hide Border';
                }
                const iconSettings = this.icons.filter(icon => icon['icon'] === TileIcons.HIDE_BORDER.icon);
                if (iconSettings && iconSettings.length) {
                    iconSettings[0]['title'] = this.tileSettings.hideBorder ? 'Show Border' : 'Hide Border';
                }
                this._tileSettingSubject.next({ type: 'CHANGE_TILE_SETTINGS' });
            } else if (icons[0].id === 'Hide Title' || icons[0].id === 'Show Title') {
                this.tileSettings.hideHeader = !this.tileSettings.hideHeader;
                const iconSettings = this.icons.filter(icon => icon['icon'] === TileIcons.HIDE_TITLE.icon);
                if (iconSettings && iconSettings.length) {
                    iconSettings[0]['title'] = this.tileSettings.hideHeader ? 'Show Title' : 'Hide Title';
                }
                this.contextMenuConfig = null;
                if (this._contextMenuSubscription && !this._contextMenuSubscription.closed) {
                    this._contextMenuSubscription.unsubscribe();
                }
                this._tileSettingSubject.next({ type: 'CHANGE_TILE_SETTINGS' });
            } else {
                this.feedbackSubject$.next({
                    type: TamDashboardEvents.ITEM_ICON_CLICK,
                    payload: {
                        id: this.id,
                        icon: icons[0].icon
                    }
                });
            }
        }
    }

    private _prepareTileSettings() {
        if (!this._widget.tile.visualization.settings) {
            this._widget.tile.visualization.settings = {};
        }
        if ((typeof this._widget.tile.visualization.settings.hideBorder) !== 'boolean') {
            this._widget.tile.visualization.settings.hideBorder = false;
        }
        if ((typeof this._widget.tile.visualization.settings.hideHeader) !== 'boolean') {
            this._widget.tile.visualization.settings.hideHeader = this.type === TamDashboardTileType.EDITOR;
        }
        return this._widget.tile.visualization.settings;
    }

    private _initIconMenuStatus() {
        this._initContextMenu();
        this._updateIconMenuText('Show Border', 'Hide Border', this.tileSettings.hideBorder);
        this._updateIconMenuText('Show Title', 'Hide Title', this.tileSettings.hideHeader);
        this._changeDetectorRef.detectChanges();
    }

    private _updateIconMenuText(trueText: string, falseText: string, currentStatus: boolean) {
        let currentText = falseText;
        let nextText = trueText;
        if (currentStatus) {
            currentText = trueText;
            nextText = falseText;
        }
        if (this.icons) {
            const icons = this.icons.filter((icon: any) => icon.title === nextText);
            if (icons && icons.length) {
                icons[0]['title'] = currentText;
            }
        }
        if (this.contextMenuConfig && this.contextMenuConfig.items) {
            const items = this.contextMenuConfig.items.filter(item => item.text === nextText);
            if (items && items.length) {
                items[0]['text'] = currentText;
            }
        }
    }
}
